import {
  FieldFieldType,
  type Form,
  type FieldsField,
  type FieldsInputField,
} from '@wix/ambassador-forms-v4-form/types';
import { type SomeJSONSchema } from 'ajv/dist/types/json-schema';
import { PropertyTypes } from '../property-types';
import { asRequired } from '../../../utils/lang';
import { ValidationMode } from '@wix/ambassador-forms-v1-validation-result/types';
import { type PlatformisedFieldToValidationTransformer } from './transform-platformised-field-to-validation';

export type FormSchemaMapperPlatformised = ReturnType<
  typeof formSchemaMapperFactoryPlatformised
>;

export function formSchemaMapperFactoryPlatformised(
  transformPlatformisedFieldToValidation: PlatformisedFieldToValidationTransformer,
  validationMode: ValidationMode,
  libraryBasedPhoneFieldValidationEnabled: boolean = false,
) {
  return function mapToFormSchema(formSchema: Form): SomeJSONSchema {
    const formFields: FieldsField[] = formSchema.formFields || [];
    const formDeletedFields: FieldsField[] = formSchema.deletedFormFields || [];
    const inputFields = formFields.filter(isInputFields);
    const deletedInputFields = formDeletedFields.filter(isInputFields);

    const properties = makeValidationProperties(
      inputFields,
      deletedInputFields,
    );

    return {
      $id: asRequired(formSchema.id, formSchema, 'id'),
      type: PropertyTypes.OBJECT,
      properties,
      additionalProperties: false,
      required: getRequiredFieldTargets(inputFields),
    };
  };

  function getRequiredFieldTargets(inputFields: FieldsField[]) {
    if (validationMode === ValidationMode.UNRESTRICTED_UPDATE) {
      return [];
    }
    return inputFields
      .filter(isVisibleEditableField)
      .map((field) => field.inputOptions!)
      .filter(isRequiredField)
      .map((inputOptions) =>
        asRequired(inputOptions.target, inputOptions, 'target'),
      );
  }

  function makeValidationProperties(
    formFields: FieldsField[],
    formDeletedFields: FieldsField[],
  ): Partial<SomeJSONSchema> {
    return [...formFields, ...formDeletedFields].reduce<
      Partial<SomeJSONSchema>
    >((acc, field) => {
      const inputField = asRequired(field.inputOptions, field, 'inputOptions');
      const property = convertFormFieldsToAjvProperties(
        asRequired(field.id, field, 'id'),
        inputField,
      );
      if (property) {
        acc[asRequired(inputField.target, field, 'target')] = property;
      }
      return acc;
    }, {});
  }

  function isVisibleEditableField(formField: FieldsField) {
    return !formField.hidden;
  }

  function isRequiredField(inputField: FieldsInputField) {
    return inputField.required ?? false;
  }

  function isInputFields(formField: FieldsField) {
    return formField.fieldType === FieldFieldType.INPUT;
  }

  function convertFormFieldsToAjvProperties(
    id: string,
    inputField: FieldsInputField,
  ): (Omit<SomeJSONSchema, '$id'> & Pick<SomeJSONSchema, '$id'>) | undefined {
    return {
      $id: id,
      ...transformPlatformisedFieldToValidation(
        inputField,
        validationMode,
        libraryBasedPhoneFieldValidationEnabled,
      ),
    };
  }
}
